
import React, { useEffect, useState } from 'react';
import DynamicForm from './signup/DynamicForm';
import { SignupFormField, SignupFormConfig } from '@/schemas/signupFormSchema';
import { UserSignupData, EnhancedSignupData } from '@/types/userSignup';
import { getGeoLocationData, detectFraud, GeoLocationData } from '@/utils/security/fraudDetection';
import { Tooltip } from '@/components/ui/tooltip';
import { AlertCircle } from 'lucide-react';

interface DynamicSignupFormProps {
  onSubmit: (data: EnhancedSignupData) => void;
  isLoading: boolean;
  formConfig?: SignupFormConfig;
}

const defaultSignupFormConfig: SignupFormConfig = {
  formTitle: "Driver Account Signup",
  fields: [
    { id: "1", name: "name", email: "name@field.com", label: "Full Name", type: "text", visible: true, required: true, defaultValue: "" },
    { id: "2", name: "email", email: "email@field.com", label: "Email", type: "email", visible: true, required: true, defaultValue: "" },
    { id: "3", name: "password", email: "password@field.com", label: "Password", type: "password", visible: true, required: true, defaultValue: "" },
    { id: "4", name: "confirmPassword", email: "confirmPassword@field.com", label: "Confirm Password", type: "password", visible: true, required: true, defaultValue: "" },
    { id: "5", name: "terms", email: "terms@field.com", label: "Terms and Conditions", type: "checkbox", visible: true, required: true, defaultValue: "" }
  ],
  ccEmail: "",
  enableCcEmail: false,
  includeTimestamp: true,
  validateAccountNumber: false,
  validateLicenseNumber: false
};

const DynamicSignupForm = ({ 
  onSubmit, 
  isLoading, 
  formConfig = defaultSignupFormConfig 
}: DynamicSignupFormProps) => {
  const [geoData, setGeoData] = useState<GeoLocationData>({});
  
  // Get geo location data when component mounts
  useEffect(() => {
    const fetchGeoData = async () => {
      const data = await getGeoLocationData();
      setGeoData(data);
    };
    
    fetchGeoData();
  }, []);
  
  const handleSubmit = (data: Record<string, any>) => {
    // Convert the generic data to UserSignupData
    const userData = data as UserSignupData;
    
    // Check for fraud
    const fraudResult = detectFraud(userData.email, geoData);
    
    // Create enhanced signup data
    const enhancedData: EnhancedSignupData = {
      ...userData,
      geoData,
      submissionTime: new Date().toISOString(),
      fraudCheck: fraudResult
    };
    
    // Pass to parent component for processing
    onSubmit(enhancedData);
  };
  
  return (
    <>
      <div className="mb-4">
        <div className="flex items-center text-sm text-gray-500">
          <span>Security Info:</span>
          {geoData.ip && (
            <span className="ml-2">
              IP: {geoData.ip} | Location: {geoData.city || 'Unknown'}, {geoData.country || 'Unknown'}
            </span>
          )}
        </div>
      </div>
      <DynamicForm
        onSubmit={handleSubmit}
        isLoading={isLoading}
        formConfig={formConfig}
      />
    </>
  );
};

export default DynamicSignupForm;
